import React, { createContext, useState } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import {
  grey,
  yellow,
  indigo,
  green,
  purple,
  teal,
} from "@mui/material/colors";

export const THEME_BLUE = "theme_blue";
const THEME_GREEN = "theme_green";
const THEME_PURPLE = "theme_purple";
const THEME_TEAL = "theme_teal";
export const Themes = [
  THEME_BLUE,
  THEME_GREEN,
  THEME_PURPLE,
  THEME_TEAL,
] as const;
export type TTheme = (typeof Themes)[number];

type TThemesContext = {
  themes: TTheme[];
  switchTheme: (theme: TTheme) => void;
  resetTheme: () => void;
};

const COLOR_ON = "on";
const COLOR_OFF = "off";
const COLOR_DISABLED = "disabled";

declare module "@mui/material/styles" {
  interface Palette {
    [COLOR_ON]: Palette["primary"];
    [COLOR_OFF]: Palette["primary"];
    [COLOR_DISABLED]: Palette["primary"];
  }

  interface PaletteColor {
    text: string;
  }

  interface PaletteOptions {
    [COLOR_ON]: PaletteOptions["primary"];
    [COLOR_OFF]: PaletteOptions["primary"];
    [COLOR_DISABLED]: PaletteOptions["primary"];
  }

  interface SimplePaletteColorOptions {
    text: string;
  }
}

declare module "@mui/material/Grid" {
  interface GridPropsColorOverrides {
    [COLOR_ON]: true;
    [COLOR_OFF]: true;
    [COLOR_DISABLED]: true;
  }
}

type TColor =
  | typeof indigo
  | typeof green
  | typeof purple
  | typeof yellow
  | typeof teal;
const makeTheme = (primaryColor: TColor, contrastColor: TColor) => {
  return createTheme({
    palette: {
      primary: primaryColor,
      [COLOR_ON]: {
        main: primaryColor[200],
        text: primaryColor[900],
        contrastText: contrastColor[400],
      },
      [COLOR_OFF]: {
        main: primaryColor[900],
        text: primaryColor[100],
        contrastText: primaryColor[300],
      },
      [COLOR_DISABLED]: {
        main: grey[400],
        text: grey[800],
        contrastText: grey[800],
      },
    },
  });
};

const themesDict = {
  [THEME_BLUE]: makeTheme(indigo, yellow),
  [THEME_GREEN]: makeTheme(green, yellow),
  [THEME_PURPLE]: makeTheme(purple, yellow),
  [THEME_TEAL]: makeTheme(teal, yellow),
};

const ThemesContext = createContext<TThemesContext | undefined>(undefined);

const LocalStorageThemeKey = "theme";

export function ThemesProvider({ children }: { children: React.ReactNode }) {
  const [themeName, setThemeName] = useState<TTheme>(
    (localStorage.getItem(LocalStorageThemeKey) as TTheme) || THEME_BLUE
  );

  const themes = Object.keys(themesDict) as TTheme[];

  const switchTheme = (newThemeName: TTheme) => {
    setThemeName(newThemeName);
    localStorage.setItem(LocalStorageThemeKey, newThemeName);
  };

  const resetTheme = () => {
    setThemeName(THEME_BLUE);
    localStorage.removeItem(LocalStorageThemeKey);
  };

  return (
    <ThemesContext.Provider value={{ themes, switchTheme, resetTheme }}>
      <ThemeProvider theme={themesDict[themeName]}>{children}</ThemeProvider>
    </ThemesContext.Provider>
  );
}

export function useThemesContext() {
  const context = React.useContext(ThemesContext);
  if (!context) {
    throw new Error("No ThemesContext found!");
  }
  return context;
}
